import { useField } from '@framework/form';
import { t } from '@framework/i18n';
import { FlexRow, VerticalSpace } from '@framework/ui';
import clx from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { hasRichTextAdvanced } from '../../../../../inputs';
import { RichTextAdvancedRender } from '../../../../../inputs/rich-text-advanced/render/RichTextAdvancedRender';
import { ConfigFormItem } from '../../../../../ui/form/ConfigFormItem';
import { parseAdvancedRichTextToString } from '../../../../../validator/utils/parseAdvancedRichTextToString';
import { WidgetScopeContextProvider } from '../../../../form/fillable/WidgetScopeContext';
import type { IWidgetComponentProps } from '../../../IWidgetType';
import { getWidgetProps } from '../../inputs/common/util/getWidgetProps';
import type { IRepeatableWidget } from './IRepeatableWidget';
import { AddRowButton } from './components/AddRowButton';
import { RemoveRowButton } from './components/RemoveRowButton';
import { useGetRepeatableChildrenDefault } from './hooks/useGetRepeatableChildrenDefault';
import { LabelLikeFormItem } from './style/LabelLikeFormItem';
import {
  AlignItemWrapper,
  RepeatableContainerWrapper,
  RowLastWrapper,
  RowParagraphWrapper,
  RowWrapper,
} from './style/RepeatableContainer';

export interface IRepeatableWidgetProps
  extends IWidgetComponentProps<IRepeatableWidget> {}

export function RepeatableWidgetComponent(props: IRepeatableWidgetProps) {
  const { widget } = props;
  const options = getWidgetProps(props);

  return (
    <ConfigFormItem
      name={options.name}
      onlyFirstLevelErrors
      readonly={options.readonly}
    >
      <RepeatableWidgetComponentContent {...props} />
    </ConfigFormItem>
  );
}

function RepeatableWidgetComponentContent(props: IRepeatableWidgetProps) {
  const { widget } = props;
  const options = getWidgetProps(props);

  const methods = useFormContext();
  const { visible, getActualSchema } = useField(options.name);
  const [alertText, setAlertText] = useState<string | null>(null);

  // Necessario per far funzionare `resetField` in caso di visibilità
  // condizionale del campo
  methods.register(options.name);
  const repeatableArrayField = useFieldArray({ name: options.name });

  const labelText = parseAdvancedRichTextToString(widget.label);

  const handleRemove = useCallback(
    (index: number) => {
      repeatableArrayField.remove(index);
      setAlertText(
        t(
          'widgets.repeatable-content.alert-sr.removed',
          'Elemento {{index}} rimosso dalla lista {{labelText}}. Gli elementi successivi sono stati riordinati.',
          { labelText, index: index + 1 },
        ),
      );
    },
    [repeatableArrayField],
  );

  const getChildrenDefault = useGetRepeatableChildrenDefault(options.name);

  const handleAdd = useCallback(() => {
    const childrenDefault = getChildrenDefault();
    repeatableArrayField.append(childrenDefault);
    setAlertText(
      t(
        'widgets.repeatable-content.alert-sr.added',
        'Elemento {{index}} aggiunto alla lista {{labelText}}.',
        { labelText, index: repeatableArrayField.fields.length + 1 },
      ),
    );
  }, [getChildrenDefault, repeatableArrayField]);

  const hideAddButton = widget.layout?.hideAddButton;
  const showLastRow = !hideAddButton || hasRichTextAdvanced(widget.description);

  const repeatableWatchedValue = useWatch({ name: options.name });

  // Se il campo è visibile e non ha un valore, vuol dire che è la prima volta
  // che viene mostrato, quindi impostiamo il valore di default.
  // Usiamo `methods.resetField` per evitare di invalidare il campo; siamo
  // costretti perché `useFieldArray` non permette di impostare un valore di
  // default (rispetto a `useController` ad esempio).
  useEffect(() => {
    if (visible && repeatableWatchedValue == null) {
      const schema = getActualSchema();
      methods.resetField(options.name, {
        defaultValue: schema?.getDefault(),
      });
    }
  }, [
    visible,
    repeatableWatchedValue,
    options.name,
    getActualSchema,
    methods.resetField,
  ]);

  return (
    <RepeatableContainerWrapper
      className={clx({ 'hide-borders': widget.layout?.hideBorder })}
    >
      <LabelLikeFormItem
        label={widget?.label}
        required={widget.rules?.repeatableRange?.min > 0}
      />
      {widget.layout?.hideBorder && <VerticalSpace />}

      <div role="alert" style={{ height: 0, overflow: 'hidden' }}>
        {alertText}
      </div>

      {repeatableArrayField.fields.map((item, index) => (
        <RowWrapper
          key={item.id}
          className={clx({ 'first-element': index === 0 })}
        >
          <FlexRow
            gutter={12}
            left={
              widget.layout?.hideNumberItem ? undefined : (
                <AlignItemWrapper>{index + 1}</AlignItemWrapper>
              )
            }
            right={
              !props.readonly &&
              !widget.layout?.hideRemoveButton && (
                <RemoveRowButton
                  widget={widget}
                  index={index}
                  onRemove={index => {
                    handleRemove(index);
                  }}
                  itemCount={repeatableArrayField.fields.length}
                />
              )
            }
          >
            {/*
             * Inserisco nel contesto il prefisso dato dal name del repeatable + l'indice del campo.
             * `option.name` permette la gestione ricorsiva del prefisso (contenendo a sua volta un possibile prefisso padre).
             */}
            <WidgetScopeContextProvider
              prefix={`${options.name}[${index}].`}
              listIndex={index}
            >
              {props.children}
            </WidgetScopeContextProvider>
          </FlexRow>
        </RowWrapper>
      ))}

      {showLastRow && (
        <RowLastWrapper
          className={clx({
            'no-list-to-show': repeatableArrayField.fields.length < 1,
          })}
        >
          <div>
            <FlexRow
              gutter={24}
              align="flex-start"
              right={
                !props.readonly && (
                  <AddRowButton
                    widget={widget}
                    itemCount={repeatableArrayField.fields.length}
                    onAdd={handleAdd}
                  />
                )
              }
            >
              {widget.description && (
                <RowParagraphWrapper>
                  <RichTextAdvancedRender
                    hasCustomFont
                    text={widget.description}
                  />
                </RowParagraphWrapper>
              )}
            </FlexRow>
          </div>
        </RowLastWrapper>
      )}
    </RepeatableContainerWrapper>
  );
}
